import { useModalLayer } from "@/contexts/ModalContext";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { type ReactNode } from "react";
import { FeedbackIcon } from "./FeedbackIcon";
import type { FeedbackVariant } from "./variants";
import { Button } from "@/components/ui/Button";
import { Scrim } from "@/components/ui/Scrim";
import { Heading, Text } from "@/components/ui/Typography";
import { dialogVariants, TRANSITION } from "@/lib/motion";
import { Surface } from "@/components/ui/Surface";

const MotionSurface = motion.create(Surface);

const MotionScrim = motion.create(Scrim);

type FeedbackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: FeedbackVariant;
  title: string;
  description?: ReactNode;
  /** Defaults to "Ok, entendi" (alert mode: single button that just closes) */
  confirmLabel?: string;
  /** When omitted, the confirm button simply closes the modal */
  onConfirm?: () => void;
  /** "danger" renders a red CTA for destructive confirmations */
  confirmTone?: "primary" | "danger";
  /** Providing a cancel label switches to confirm mode (two stacked buttons) */
  cancelLabel?: string;
  /** Disables buttons and shows a spinner inside the CTA */
  loading?: boolean;
  /** When false, backdrop click and Escape do not close (default true) */
  dismissible?: boolean;
  /** Extra content between the description and the buttons (inputs, etc.) */
  children?: ReactNode;
  /** Replaces the default button footer entirely (e.g. 3 custom actions) */
  actions?: ReactNode;
};

export function FeedbackModal({
  open,
  onOpenChange,
  variant,
  title,
  description,
  confirmLabel = "Ok, entendi",
  onConfirm,
  confirmTone = "primary",
  cancelLabel,
  loading = false,
  dismissible = true,
  children,
  actions,
}: FeedbackModalProps) {
  const reduceMotion = useReducedMotion();
  const canDismiss = dismissible && !loading;
  const { zIndex, backdropZIndex } = useModalLayer(open);
  useLockBodyScroll(open);

  function close() {
    onOpenChange(false);
  }

  function handleConfirm() {
    if (onConfirm) {
      onConfirm();
    } else {
      close();
    }
  }

  return (
    <Dialog.Root
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next && !canDismiss) return;
        onOpenChange(next);
      }}
    >
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <MotionScrim
              aria-hidden
              zIndex={backdropZIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={TRANSITION.fade}
              onClick={() => {
                if (canDismiss) close();
              }}
            />
            <Dialog.Content
              forceMount
              asChild
              aria-describedby={description ? undefined : ""}
              onEscapeKeyDown={(event) => {
                if (!canDismiss) event.preventDefault();
              }}
              onPointerDownOutside={(event) => event.preventDefault()}
              onInteractOutside={(event) => event.preventDefault()}
            >
              <div style={{ zIndex }} className="pointer-events-none fixed inset-0 grid place-items-center p-6">
                <MotionSurface
                  surface="card"
                  className="pointer-events-auto flex w-full max-w-sm flex-col items-center gap-4 rounded-sheet p-6 pt-8 text-center shadow-overlay"
                  variants={dialogVariants(reduceMotion)}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <FeedbackIcon variant={variant} />
                  <div className="flex flex-col gap-2">
                    <Dialog.Title asChild>
                      <Heading level={2} size="title">
                        {title}
                      </Heading>
                    </Dialog.Title>
                    {description && (
                      <Dialog.Description asChild>
                        <Text as="div" leading="relaxed" tone="muted">
                          {description}
                        </Text>
                      </Dialog.Description>
                    )}
                  </div>
                  {children}
                  <div className="mt-2 flex w-full flex-col gap-2">
                    {actions ?? (
                      <>
                        <Button
                          tone={confirmTone === "danger" ? "danger" : "primary"}
                          fullWidth
                          isLoading={loading}
                          onClick={handleConfirm}
                        >
                          {confirmLabel}
                        </Button>
                        {cancelLabel && (
                          <Button
                            variant="ghost"
                            tone="neutral"
                            fullWidth
                            onClick={close}
                            disabled={loading}
                          >
                            {cancelLabel}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </MotionSurface>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
